/**
 * Long prose, bounded by a disclosure rather than by a scroll pane.
 *
 * It accepts no `maxHeight`, never sets `overflow`, and owns no scrollbar.
 * A 120px scroll box inside a card is a second scroll context competing with
 * the page's, and it hides text behind a gesture a reader has no reason to
 * expect. A disclosure states that there is more and lets them ask.
 *
 * The trigger is inline at the end of the clamped text and stays in flow, so
 * expanding does not move it out from under the pointer. It becomes `Read
 * less` in place. The ONE exception is a line-limited body: a CSS line clamp is
 * `overflow: hidden` on the paragraph, and a control inside that paragraph is
 * clipped along with the text it was meant to reveal — a disclosure with no way
 * to open it. Under `Lines` the trigger is therefore a sibling of the paragraph
 * rather than a child of it, in both states, so it does not move when pressed.
 *
 * Two modes, because a captured one-line condition and a 4,000-character
 * abstract are different problems: `Display` sets a short captured string at
 * heading scale; `Reading` bounds a body at a reading measure. They are a
 * discriminated union rather than one props shape with a mode flag, because
 * everything the disclosure needs — both labels, the visibility rule, the drop
 * cap, the footnote, the measure — is dead weight in `Display`, and a required
 * `expandLabel` that Display mode never renders is a prop passed for the type
 * rather than for the reader.
 */
"use client";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactElement,
} from "react";
import { Cbutton } from "@/components/ui/Cbutton";
import { ButtonPreset } from "@/constants/componentPresets";
import { useCrcTouchExpansion } from "./cresultcard.touch";
import {
  CRC_TEXT,
  CRC_TEXT_FAINT,
  CRC_TYPE_STYLE,
  crcClampLines,
} from "./cresultcard.tokens";
import {
  CresultcardProseMode,
  CresultcardProseVisible,
  CresultcardTextStep,
} from "./cresultcard.types";
import { useCresultcardViewport } from "./cresultcard.viewport";

/** 62 characters is the measure prose stops being a wall at. */
const DEFAULT_MEASURE_CH = 62;
const ELLIPSIS = "… ";
/** Models and vendors both emit CRLF; a paragraph break is either form. */
const PARAGRAPH_BREAK = /\r?\n\s*\r?\n/;
const SENTENCE_END = /(?<=[.!?])\s+/;
const DROP_CAP_SIZE = 30;
/** Logical float, so a right-to-left body drops its cap on the correct edge. */
const DROP_CAP_FLOAT = "inline-start";
const TOGGLE_ROOT_ATTRIBUTES = {
  "data-el": "Cresultcard/Prose/Trigger",
  "data-crc-control": true,
};
const TOGGLE_TEXT_SIZE = 11;
/** A sub-pixel rounding difference is not text held back. */
const CLAMP_OVERFLOW_TOLERANCE = 1;
/** Out of the paragraph, the trigger is a flex item; it holds its own width. */
const TOGGLE_BLOCK_ALIGN_SELF: CSSProperties["alignSelf"] = "flex-start";

interface CresultcardProseDisplayProps {
  readonly mode: CresultcardProseMode.Display;
  readonly text: string;
  /**
   * Display mode's type step. It was pinned to `Lead`, which is 17px — exactly
   * the size of the `Rail` figure step — so a card built from a rail figure and
   * a display string had its prose shouting as loudly as its one number. The
   * default is still `Lead`, so a caller that says nothing is unchanged.
   */
  readonly displayStep?: CresultcardTextStep;
}

interface CresultcardProseReadingProps {
  readonly mode: CresultcardProseMode.Reading;
  readonly text: string;
  /** How much is shown before the disclosure. */
  readonly visible?: CresultcardProseVisible;
  /** With `Lines`: how many. */
  readonly lines?: number;
  readonly measure?: number;
  readonly expandLabel: string;
  readonly collapseLabel: string;
  readonly dropCap?: boolean;
  readonly footnote?: string;
}

export type CresultcardProseProps =
  | CresultcardProseDisplayProps
  | CresultcardProseReadingProps;

interface SplitProseInput {
  readonly text: string;
  readonly visible: CresultcardProseVisible;
}

/** The part shown while collapsed, and whether anything was held back. */
function splitProse({ text, visible }: SplitProseInput): string {
  if (visible === CresultcardProseVisible.FirstParagraph) {
    return text.split(PARAGRAPH_BREAK)[0] ?? text;
  }
  if (visible === CresultcardProseVisible.FirstSentence) {
    return text.split(SENTENCE_END)[0] ?? text;
  }
  return text;
}

interface ToggleLabelInput {
  readonly expanded: boolean;
  readonly expandLabel: string;
  readonly collapseLabel: string;
}

/**
 * The trigger says what it does and nothing else. It used to carry the body's
 * length — `Read more · 111 chars` — which is a bare cardinality about content
 * already on the surface, and §3.7 forbids exactly that: a character count is
 * arithmetic about the text, not a fact that changes what a reader does with
 * it.
 */
function toggleLabel({
  expanded,
  expandLabel,
  collapseLabel,
}: ToggleLabelInput): string {
  return expanded ? collapseLabel : expandLabel;
}

function CresultcardProseDisplay({
  text,
  displayStep = CresultcardTextStep.Lead,
}: CresultcardProseDisplayProps): ReactElement {
  return (
    <p
      data-el="Cresultcard/ProseDisplay/Root"
      style={{
        ...CRC_TYPE_STYLE[displayStep],
        margin: 0,
        minWidth: 0,
      }}
    >
      {text}
    </p>
  );
}

function CresultcardProseReading({
  text,
  visible = CresultcardProseVisible.FirstParagraph,
  lines,
  measure = DEFAULT_MEASURE_CH,
  expandLabel,
  collapseLabel,
  dropCap = false,
  footnote,
}: CresultcardProseReadingProps): ReactElement {
  const [expanded, setExpanded] = useState(false);
  /**
   * Whether the clamp is actually holding text back. A line clamp cannot be
   * asked in advance — it depends on the measure, the family and the wrap — so
   * the body is measured, and the answer sticks until the text changes. Without
   * it every short string in `Lines` mode grows a `Read more` control with
   * nothing behind it.
   */
  const [clampOverflows, setClampOverflows] = useState(false);
  const bodyRef = useRef<HTMLParagraphElement | null>(null);
  const { isMobile } = useCresultcardViewport();
  const toggleTouch = useCrcTouchExpansion({ isMobile });
  const toggle = useCallback((): void => {
    setExpanded((current) => !current);
  }, []);

  const measureClamp = useCallback((): void => {
    const node = bodyRef.current;
    if (node == null) {
      return;
    }
    setClampOverflows(
      node.scrollHeight > node.clientHeight + CLAMP_OVERFLOW_TOLERANCE,
    );
  }, []);

  useEffect((): (() => void) | undefined => {
    const body = bodyRef.current;
    // Measuring an expanded body would find no overflow, because there is no
    // clamp to overflow — and would then withdraw the `Read less` control.
    if (body == null || expanded) {
      return undefined;
    }
    if (typeof ResizeObserver === "undefined") {
      const frame = window.requestAnimationFrame(measureClamp);
      return (): void => {
        window.cancelAnimationFrame(frame);
      };
    }
    // `observe` delivers an initial observation of its own, so the first
    // measurement arrives without this effect reaching for the DOM inline.
    const observer = new ResizeObserver(measureClamp);
    observer.observe(body);
    return (): void => {
      observer.disconnect();
    };
  }, [expanded, measureClamp, text]);

  const clampsByLines =
    visible === CresultcardProseVisible.Lines && lines != null;
  const clampToLines = clampsByLines && !expanded;
  const collapsedText = splitProse({ text, visible });
  // Whether there is more to READ, which does not change when the reader
  // expands: the `Read less` control has to survive its own press.
  const hasMore =
    collapsedText.length < text.length || (clampsByLines && clampOverflows);
  const shown = expanded || clampToLines ? text : collapsedText;
  const bodyStyle: CSSProperties = {
    ...CRC_TYPE_STYLE[CresultcardTextStep.Body],
    ...(clampToLines && lines != null ? crcClampLines(lines) : {}),
    margin: 0,
    maxWidth: `${String(measure)}ch`,
    minWidth: 0,
    // Vendor bodies arrive pre-wrapped as often as not; honouring the wrap is
    // the difference between a paragraph and a ragged column of short lines.
    whiteSpace: "pre-wrap",
    color: CRC_TEXT,
  };
  /**
   * SPEC §7.4's minimum target, taken as padding on the inline root. Vertical
   * padding on an inline box does not enter its line box, so the trigger's hit
   * area reaches the floor without moving one line of the paragraph it sits in.
   * The band is measured rather than declared: the toggle's line box is 18.59px,
   * a declared 14 bought 4.6px of padding the control did not need.
   */
  const toggleRootStyle: CSSProperties = {
    ...toggleTouch.style,
    alignSelf: clampsByLines ? TOGGLE_BLOCK_ALIGN_SELF : undefined,
  };

  const toggleNode = hasMore ? (
    <Cbutton
      ariaLabel={expanded ? collapseLabel : expandLabel}
      disableClickMotion
      inline
      inlineRootRef={toggleTouch.measureRoot}
      label={toggleLabel({ collapseLabel, expandLabel, expanded })}
      onClick={toggle}
      padding={0}
      preset={ButtonPreset.Ghost}
      radius={0}
      rootAttributes={{
        ...TOGGLE_ROOT_ATTRIBUTES,
        "aria-expanded": expanded,
      }}
      style={toggleRootStyle}
      textSize={TOGGLE_TEXT_SIZE}
    />
  ) : null;

  return (
    <div
      data-el="Cresultcard/ProseReading/Root"
      style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}
    >
      <p
        ref={bodyRef}
        style={bodyStyle}
      >
        {dropCap && (
          <span
            style={{
              float: DROP_CAP_FLOAT,
              paddingInlineEnd: 6,
              fontSize: DROP_CAP_SIZE,
              fontWeight: 780,
              lineHeight: 0.92,
              color: CRC_TEXT,
            }}
          >
            {shown.slice(0, 1)}
          </span>
        )}
        {dropCap ? shown.slice(1) : shown}
        {!expanded && hasMore && !clampToLines ? ELLIPSIS : null}
        {clampsByLines ? null : toggleNode}
      </p>
      {clampsByLines ? toggleNode : null}
      {footnote != null && (
        <span
          style={{
            fontSize: 9.5,
            fontWeight: 540,
            color: CRC_TEXT_FAINT,
          }}
        >
          {footnote}
        </span>
      )}
    </div>
  );
}

export function CresultcardProse(props: CresultcardProseProps): ReactElement {
  return props.mode === CresultcardProseMode.Display ? (
    <CresultcardProseDisplay {...props} />
  ) : (
    <CresultcardProseReading {...props} />
  );
}
